'use client';

import Image from 'next/image';
import { FC, useState, useEffect, useRef, useCallback } from 'react';

import { SerializedFile } from '@/src/utils/serializeFile';

interface MediaPreviewProps {
  activeFile: SerializedFile | null;
  onMediaLoad: () => void;
  className?: string;
  mediaType: 'image' | 'video' | 'unknown';
}

interface MediaLoadState {
  isLoaded: boolean;
  hasError: boolean;
  isLoading: boolean;
}

const STYLES = {
  container:
    'relative shrink-0 flex flex-col items-center justify-end w-full h-[335px] rounded-xl overflow-hidden',
  media:
    'pointer-events-none block size-full translate-z-0 absolute top-0 z-1 object-contain select-none',
  loadingSkeleton: 'absolute z-10 inset-0 animate-skeleton-pulse bg-gray-200',
  backgroundImage:
    'pointer-events-none block absolute top-0 z-0 size-full translate-z-0 object-cover select-none',
  errorState:
    'absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-500 z-10',
} as const;

const BACKGROUND_IMAGE = {
  src: '/images/inpaint-bg.webp',
  width: 826,
  height: 826,
  quality: 95,
} as const;

const MEDIA_CONFIG = {
  image: {
    width: 826,
    height: 826,
    quality: 100,
  },
  video: {
    preload: 'metadata' as const,
    attributes: {
      autoPlay: true,
      loop: true,
      muted: true,
      playsInline: true,
      'webkit-playsinline': 'true' as const,
    },
  },
} as const;

// Custom hook for media loading state management
const useMediaLoader = (src: string | undefined, onLoad: () => void) => {
  const [loadState, setLoadState] = useState<MediaLoadState>({
    isLoaded: false,
    hasError: false,
    isLoading: false,
  });

  const handleLoadSuccess = useCallback(() => {
    setLoadState({ isLoaded: true, hasError: false, isLoading: false });
    onLoad();
  }, [onLoad]);

  const handleLoadError = useCallback(() => {
    setLoadState({ isLoaded: false, hasError: true, isLoading: false });
  }, []);

  const startLoading = useCallback(() => {
    setLoadState({ isLoaded: false, hasError: false, isLoading: true });
  }, []);

  // Reset state when source changes
  useEffect(() => {
    if (src) {
      startLoading();
    } else {
      setLoadState({ isLoaded: false, hasError: false, isLoading: false });
    }
  }, [src, startLoading]);

  return {
    ...loadState,
    handleLoadSuccess,
    handleLoadError,
  };
};

// Custom hook for video autoplay
const useVideoAutoplay = (
  videoRef: React.RefObject<HTMLVideoElement | null>,
  shouldPlay: boolean,
) => {
  const [hasPlayAttempted, setHasPlayAttempted] = useState(false);

  useEffect(() => {
    if (!shouldPlay || hasPlayAttempted || !videoRef.current) {
      return;
    }

    const playVideo = async () => {
      try {
        await videoRef.current?.play();
        setHasPlayAttempted(true);
      } catch (error) {
        console.warn('Video autoplay failed:', error);
        setHasPlayAttempted(true);
      }
    };

    playVideo();
  }, [shouldPlay, hasPlayAttempted, videoRef]);

  // Reset play attempt when video source changes
  useEffect(() => {
    setHasPlayAttempted(false);
  }, [shouldPlay]);
};

const VideoPlayer: FC<{
  src: string;
  onLoad: () => void;
  onError: () => void;
  className: string;
}> = ({ src, onLoad, onError, className }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useVideoAutoplay(videoRef, true);

  return (
    <video
      ref={videoRef}
      className={className}
      onLoadedData={onLoad}
      onError={onError}
      preload={MEDIA_CONFIG.video.preload}
      {...MEDIA_CONFIG.video.attributes}
      aria-label="Media preview video"
    >
      <source src={src} />
      Your browser does not support the video tag.
    </video>
  );
};

const ImagePreview: FC<{
  src: string;
  onLoad: () => void;
  onError: () => void;
  className: string;
}> = ({ src, onLoad, onError, className }) => (
  <Image
    src={src}
    width={MEDIA_CONFIG.image.width}
    height={MEDIA_CONFIG.image.height}
    quality={MEDIA_CONFIG.image.quality}
    priority
    alt="Media preview"
    className={className}
    onLoad={onLoad}
    onError={onError}
  />
);

const LoadingState: FC = () => (
  <div className={STYLES.loadingSkeleton} aria-label="Loading media..." />
);

const ErrorState: FC<{ mediaType: string }> = ({ mediaType }) => (
  <div className={STYLES.errorState} role="alert">
    <span>Failed to load {mediaType}</span>
  </div>
);

export const MediaPreview: FC<MediaPreviewProps> = ({
  activeFile,
  onMediaLoad,
  mediaType,
  className = '',
}) => {
  const { isLoaded, hasError, isLoading, handleLoadSuccess, handleLoadError } =
    useMediaLoader(activeFile?.src, onMediaLoad);

  // Early return if no file
  if (!activeFile?.src) {
    return null;
  }

  const containerClasses = `${STYLES.container} ${className}`.trim();

  return (
    <div
      className={containerClasses}
      role="img"
      aria-label="Media preview container"
    >
      {/* Background Image */}
      <Image
        src={BACKGROUND_IMAGE.src}
        width={BACKGROUND_IMAGE.width}
        height={BACKGROUND_IMAGE.height}
        quality={BACKGROUND_IMAGE.quality}
        priority
        alt=""
        className={STYLES.backgroundImage}
        aria-hidden="true"
      />

      {/* Loading State */}
      {isLoading && !isLoaded && <LoadingState />}

      {/* Error State */}
      {hasError && <ErrorState mediaType={mediaType} />}

      {/* Media Content */}
      {!hasError && (
        <>
          {mediaType === 'video' ? (
            <VideoPlayer
              src={activeFile.src}
              onLoad={handleLoadSuccess}
              onError={handleLoadError}
              className={STYLES.media}
            />
          ) : mediaType === 'image' ? (
            <ImagePreview
              src={activeFile.src}
              onLoad={handleLoadSuccess}
              onError={handleLoadError}
              className={STYLES.media}
            />
          ) : (
            <ErrorState mediaType="unsupported file type" />
          )}
        </>
      )}
    </div>
  );
};
